import { ColorFormatResult, formatColor, hslResult, rgbToHsl } from '@shared/utils/parsers';
import { usePointer } from '@shared/utils/UseEvent';
import { reactive, ref, Ref } from 'vue';

type HSLArray = [number, number, number, number?];

export function useColorManager(parsedColor: { values: [number, number, number, (number | undefined)?]; string: string }) {
    const gradientRef: Ref<HTMLElement | undefined> = ref();
    const clr = reactive({
        hue: parsedColor.values[0],
        saturation: parsedColor.values[1],
        lightness: parsedColor.values[2],
        alpha: parsedColor.values[3] ?? 100,
    });
    const state = reactive({
        x: 0,
        y: 0,
    });

    const getValuesFromPoint = (ev: PointerEvent) => {
        let gradientBox = gradientRef.value!,
            height = gradientBox.offsetHeight,
            width = gradientBox.offsetWidth,
            hm = 100 / height, // height modifier
            wm = 100 / width, // width modifier
            pph = height / 100, // pixels per 1% height
            ppw = width / 100, // pixels per 1% width
            point = {
                x: ev.offsetX,
                y: ev.offsetY,
            };

        const setOffset = (point: { x: number; y: number }) => {
            let x = point.x * wm,
                y = point.y * hm;

            if (point.x >= 0 && point.x <= width) {
                state.x = point.x;
                clr.saturation = ~~x;
            } else if (point.x < 0) clr.saturation = 0;
            else if (point.x > width) clr.saturation = 100;

            x = x > 100 ? 100 : x < 0 ? 0 : x;

            if (point.y >= 0 && point.y <= height) {
                clr.lightness = ~~(((100 - x / 2 - 0.5) / 100) * (100 - y));
                state.y = point.y;
            } else if (point.y < 0) clr.lightness = ~~(100 - x / 2 - 0.5);
            else if (point.y > height) clr.lightness = 0;
        };

        setOffset(point);

        usePointer.move(({ tgtOffset: point }) => {
            setOffset(point);
        }, ev);
    };

    const switchColor = (index: number, selectedColorIndex: Ref<number, number>, colors: { color: string | HSLArray; name: string }[]) => {
        selectedColorIndex.value = index;
        const newColor = colors[index];
        const result = typeof newColor.color === 'string' ? (formatColor(newColor.color, 'hsl') as ColorFormatResult<HSLArray>) : hslResult(newColor.color);

        clr.hue = result.values[0];
        clr.saturation = result.values[1];
        clr.lightness = result.values[2];
        clr.alpha = result.values[3] ?? 100;
        updatePointerPosition();
    };

    const applyColorFromInput = (colorInput: string) => {
        try {
            let result;
            if (colorInput.startsWith('rgb')) {
                const rgbValues = colorInput
                    .replace(/[^\d,]/g, '')
                    .split(',')
                    .map((v) => parseInt(v.trim(), 10));
                if (rgbValues.length !== 3 || rgbValues.some((v) => isNaN(v) || v < 0 || v > 255)) {
                    throw new Error('Invalid RGB format');
                }
                result = rgbToHsl([rgbValues[0], rgbValues[1], rgbValues[2]]); // Konvertera till HSL
            } else {
                result = formatColor(colorInput, 'hsl') as ColorFormatResult<HSLArray>;
            }

            clr.hue = result.values[0];
            clr.saturation = result.values[1];
            clr.lightness = result.values[2];
            clr.alpha = result.values[3] ?? 100;

            updatePointerPosition();
            console.log('Färg uppdaterad från input:', clr);
        } catch (error) {
            console.error('Invalid color input:', error);
        }
    };

    function updatePointerPosition() {
        const gradientBox = gradientRef.value;
        if (!gradientBox) return;

        const width = gradientBox.offsetWidth; // Bredd på gradient
        const height = gradientBox.offsetHeight; // Höjd på gradient

        // Saturation till x-koordinat
        state.x = (clr.saturation / 100) * width;

        // Lightness till y-koordinat
        const lightnessRatio = 100 - clr.lightness; // Inverterad lightness
        state.y = (lightnessRatio / 100) * height;
    }

    return {
        gradientRef,
        getValuesFromPoint,
        switchColor,
        applyColorFromInput,
        clr,
        state,
        updatePointerPosition,
    };
}
